import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AssetPrivilegeTypeStringify,
  DevicePrivilegeTypeStringify,
  DocumentsPrivilegeTypeStringify,
  GeneralPrivilegeTypeStringify,
  RoomPrivilegeTypeStringify,
  ScenePrivilegeTypeStringify,
  TicketPrivilegeTypeStringify
} from '@simOn/privileges/element/models';
import { Observable, filter, switchMap } from 'rxjs';
import { ClearPrivilegesState, loadPrivileges } from './privileges.actions';
import {
  assetPrivileges,
  canCreateAssetsInAnyRoom,
  generalPrivileges,
  getRoomsPrivileges,
  hasAssetAccessTo,
  hasDeviceAccessTo,
  hasDocumentsAccessTo,
  hasGeneralAccessTo,
  hasInRoomAccessTo,
  hasSceneAccessTo,
  hasTicketAccessTo,
  isAdmin,
  isOwner,
  isOwnerOrAdmin,
  privilegesLoaded,
  roomPrivileges,
  roomsWithPermission,
  userGuid
} from './privileges.selectors';

let z = 1;

@Injectable({
  providedIn: 'root'
})
export class PrivilegesFacade {
  private readonly store = inject(Store);
  readonly isOwner$: Observable<boolean> = this.store.select(isOwner);
  readonly isAdmin$: Observable<boolean> = this.store.select(isAdmin);
  readonly isOwnerOrAdmin$: Observable<boolean> = this.store.select(isOwnerOrAdmin);

  readonly privilegesLoaded$ = this.store.select(privilegesLoaded);

  readonly userGuid$: Observable<string> = this.privilegesLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    switchMap(() => this.store.select(userGuid))
  );

  readonly generalPrivileges$ = this.privilegesLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    switchMap(() => this.store.select(generalPrivileges))
  );
  readonly roomPrivileges$ = this.privilegesLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    switchMap(() => this.store.select(getRoomsPrivileges))
  );

  readonly hasGeneralAccessTo$ = (privilege: GeneralPrivilegeTypeStringify): Observable<boolean> =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(hasGeneralAccessTo(privilege)))
    );

  readonly hasAssetAccessTo$ = (assetId: string, privilege: AssetPrivilegeTypeStringify): Observable<boolean> =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(hasAssetAccessTo(assetId, privilege)))
    );

  readonly hasTicketAccessTo$ = (ticketId: string, privilege: TicketPrivilegeTypeStringify): Observable<boolean> =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(hasTicketAccessTo(ticketId, privilege)))
    );

  readonly hasSceneAccessTo$ = (sceneId: string, privilege: ScenePrivilegeTypeStringify): Observable<boolean> =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(hasSceneAccessTo(sceneId, privilege)))
    );

  readonly hasDeviceAccessTo$ = (deviceId: string, privilege: DevicePrivilegeTypeStringify): Observable<boolean> =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(hasDeviceAccessTo(deviceId, privilege)))
    );

  readonly hasDocumentsAccessTo$ = (privilege: DocumentsPrivilegeTypeStringify): Observable<boolean> =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(hasDocumentsAccessTo(privilege)))
    );

  readonly hasInRoomAccessTo$ = (roomId: string, privilege: RoomPrivilegeTypeStringify): Observable<boolean> =>
    this.store.select(hasInRoomAccessTo(roomId, privilege));

  readonly canCreateAssetsInAnyRoom$ = (): Observable<boolean> => this.store.select(canCreateAssetsInAnyRoom());

  readonly selectRoomsWithPermission$ = (privilege: RoomPrivilegeTypeStringify): Observable<string[]> =>
    this.store.select(roomsWithPermission(privilege));

  readonly getRoomPrivileges$ = (roomId: string) => this.store.select(roomPrivileges(roomId));

  readonly getAssetPrivileges$ = (assetId: string) =>
    this.privilegesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this.store.select(assetPrivileges(assetId)))
    );

  constructor() {
    this.generalPrivileges$.subscribe((privileges) => {
      console.log('General Privileges:', privileges);
    });
  }

  loadPrivileges(): void {
    this.store.dispatch(loadPrivileges());
  }
  clearState() {
    this.store.dispatch(ClearPrivilegesState());
  }
}
